/**
 * Pure helpers of the Catalog Review workspace: filter vocabularies, integrity summaries, source evidence links and
 * labels. No React, so the page, its tables and the tests share one copy.
 */
import { safeHref } from '../../../lib/safe-url.js';
import type {
  CatalogCandidate,
  CatalogCandidateDecision,
  CatalogCandidateListItem,
  CatalogEvidenceLinks,
  CatalogIntegrityCheck,
  CatalogItemVersion,
  CatalogSource,
  CatalogSourceFragment,
  DaemonReviewQueueItem,
} from './catalog-types.js';

export const STATUSES = [
  'ready_for_human',
  'pending_security',
  'pending_quality',
  'security_failed',
  'quality_failed',
  'applied',
] as const;
export const TARGET_TYPES = [
  'model',
  'model_capability',
  'agent_template',
  'skill',
  'tool',
  'workflow',
  'evaluation',
] as const;
export const TARGET_TYPE_LABELS: Record<string, string> = {
  model: 'Models',
  model_capability: 'Model Capabilities',
  agent_template: 'Agent Templates',
  skill: 'Skills',
  tool: 'Tools',
  workflow: 'Workflows',
  evaluation: 'Evaluations',
};

export function formatCatalogAdoptionLabel(value: string) {
  return value.replaceAll('daemon', 'workforce').replaceAll('_', ' ');
}

export function groupQueueByDaemon(
  items: DaemonReviewQueueItem[],
): Array<{ daemonName: string; items: DaemonReviewQueueItem[] }> {
  const grouped = new Map<string, DaemonReviewQueueItem[]>();
  for (const item of items) {
    const bucket = grouped.get(item.targetDaemonName) ?? [];
    bucket.push(item);
    grouped.set(item.targetDaemonName, bucket);
  }
  return Array.from(grouped.entries()).map(([daemonName, daemonItems]) => ({ daemonName, items: daemonItems }));
}

export type CatalogCandidateIntegrityState = {
  sourceFragment: CatalogSourceFragment | null;
  blockingChecks: CatalogIntegrityCheck[];
  warningChecks: CatalogIntegrityCheck[];
  hasBlockingChecks: boolean;
  isSourceOnly: boolean;
  badgeLabel: string | null;
  summary: string | null;
  recommendation: string | null;
};

export type CatalogSourceIntegrityState = {
  sourceFragment: CatalogSourceFragment | null;
  warningChecks: number;
  hasBlockingChecks: boolean;
  isSourceOnly: boolean;
  badgeLabel: string | null;
  summary: string | null;
  recommendation: string | null;
};

export function summarizeCatalogReviewAudit({
  candidates,
  sources,
  queueItems,
  versions,
}: {
  candidates: Array<Pick<CatalogCandidateListItem, 'status' | 'integrityChecks' | 'evidenceLinks'>>;
  sources: Array<Pick<CatalogSource, 'fragments' | 'integrity' | 'metadata'>>;
  queueItems: Array<Pick<DaemonReviewQueueItem, 'status'>>;
  versions: CatalogItemVersion[];
}) {
  const integrityBlockedCandidates = candidates.filter(
    (candidate) => describeCatalogCandidateIntegrity(candidate).hasBlockingChecks,
  ).length;
  const integrityBlockedSources = sources.filter(
    (source) => describeCatalogSourceIntegrity(source).hasBlockingChecks,
  ).length;

  return {
    blockedReviews: queueItems.filter((item) => item.status === 'blocked').length,
    failedReviews: queueItems.filter((item) => item.status === 'failed').length,
    failedCandidates: candidates.filter(
      (candidate) => candidate.status === 'security_failed' || candidate.status === 'quality_failed',
    ).length,
    readyCandidates: candidates.filter(
      (candidate) => candidate.status === 'ready_for_human' || candidate.status === 'approved',
    ).length,
    pendingCandidates: candidates.filter((candidate) => candidate.status.startsWith('pending')).length,
    sourceFragments: sources.reduce((total, source) => total + (source.fragments?.length ?? 0), 0),
    integrityBlocked: integrityBlockedCandidates + integrityBlockedSources,
    promotedVersions: versions.length,
  };
}

export function catalogSourceFragmentLabel(fragment: CatalogSourceFragment): string {
  return (
    firstNonEmpty(fragment.title, fragment.sourcePath, fragment.sourceUrl, fragment.targetTypeHint) ??
    fragment.fragmentType.replaceAll('_', ' ')
  );
}

export function catalogSourceFragmentMeta(fragment: CatalogSourceFragment): string {
  const parts = [
    fragment.sourcePath ? `Path: ${fragment.sourcePath}` : null,
    fragment.sourceRef ? `Ref: ${fragment.sourceRef}` : null,
    fragment.contentType ? `Type: ${fragment.contentType}` : null,
  ].filter(Boolean);
  return parts.join(' / ') || 'No fragment path or ref recorded.';
}

export function describeCatalogCandidateIntegrity(
  candidate: Pick<CatalogCandidateListItem, 'integrityChecks' | 'evidenceLinks'>,
): CatalogCandidateIntegrityState {
  const blockingChecks = (candidate.integrityChecks ?? []).filter((check) => check.status === 'blocked');
  const warningChecks = (candidate.integrityChecks ?? []).filter((check) => check.status === 'warning');
  const sourceFragment = findCatalogSourceFragment(candidate);
  const isSourceOnly = sourceFragment?.classification === 'source_only';
  const primaryCheck = blockingChecks[0] ?? warningChecks[0] ?? null;

  let badgeLabel: string | null = null;
  let summary: string | null = null;
  if (blockingChecks.length > 0 && isSourceOnly) {
    badgeLabel = 'Source-only discovery';
    summary =
      primaryCheck?.message ??
      'The source was captured, but this discovery is still evidence-only until a concrete fragment and payload are extracted.';
  } else if (blockingChecks.length > 0) {
    badgeLabel = 'Integrity blocked';
    summary =
      primaryCheck?.message ?? 'Catalog integrity checks blocked this candidate until the source data is repaired.';
  } else if (isSourceOnly) {
    badgeLabel = 'Source evidence only';
    summary =
      sourceFragment?.rationale ??
      'This discovery currently points to source evidence rather than a promotion-ready catalog entity.';
  } else if (warningChecks.length > 0) {
    badgeLabel = 'Provenance warning';
    summary =
      warningChecks[0]?.message ??
      'Source provenance is present, but one or more warnings should be reviewed before promotion.';
  }

  return {
    sourceFragment,
    blockingChecks,
    warningChecks,
    hasBlockingChecks: blockingChecks.length > 0,
    isSourceOnly,
    badgeLabel,
    summary,
    recommendation: primaryCheck?.recommendation ?? null,
  };
}

export function describeCatalogSourceIntegrity(
  source: Pick<CatalogSource, 'fragments' | 'integrity' | 'metadata'>,
): CatalogSourceIntegrityState {
  const sourceOnlyFragment = source.fragments?.find((fragment) => fragment.classification === 'source_only') ?? null;
  const hasLibraryCandidateFragment = Boolean(
    source.fragments?.some((fragment) => fragment.classification === 'library_candidate'),
  );
  const latestCheck = source.integrity?.latest ?? null;
  const hasBlockingChecks = Boolean(source.integrity && source.integrity.blocked > 0);
  const warningChecks = source.integrity?.warnings ?? 0;
  const isSourceOnly =
    source.metadata?.sourceOnly === true ||
    Boolean(sourceOnlyFragment) ||
    (hasBlockingChecks && !hasLibraryCandidateFragment);
  const primaryFragment = sourceOnlyFragment ?? latestCheck?.sourceFragment ?? source.fragments?.[0] ?? null;

  let badgeLabel: string | null = null;
  let summary: string | null = null;
  if (hasBlockingChecks && isSourceOnly) {
    badgeLabel = 'Source-only discovery';
    summary =
      latestCheck?.message ??
      sourceOnlyFragment?.rationale ??
      'The source was retained for catalog review, but it needs fragment extraction before promotion.';
  } else if (hasBlockingChecks) {
    badgeLabel = 'Integrity blocked';
    summary = latestCheck?.message ?? 'Catalog integrity checks blocked this source from candidate promotion.';
  } else if (isSourceOnly) {
    badgeLabel = 'Source evidence only';
    summary =
      sourceOnlyFragment?.rationale ??
      'This source is available for catalog review but does not yet contain a promotion-ready entity payload.';
  } else if (warningChecks > 0) {
    badgeLabel = 'Provenance warning';
    summary = latestCheck?.message ?? 'One or more source provenance warnings should be reviewed.';
  }

  return {
    sourceFragment: primaryFragment,
    warningChecks,
    hasBlockingChecks,
    isSourceOnly,
    badgeLabel,
    summary,
    recommendation: latestCheck?.recommendation ?? sourceOnlyFragment?.rationale ?? null,
  };
}

export function candidateLabel(candidate: CatalogCandidateListItem | CatalogCandidateDecision): string {
  const payload = candidate.payload ?? {};
  for (const key of ['name', 'title', 'slug', 'id', 'modelId']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  if (candidate.diffSummary?.trim()) return candidate.diffSummary;
  if (candidate.source?.title?.trim()) return candidate.source.title;
  return `${candidate.targetType} candidate ${candidate.id}`;
}

export function candidateValueText(candidate: CatalogCandidate): string {
  const payload = candidate.payload ?? {};
  if (!payload || typeof payload !== 'object' || Array.isArray(payload) || Object.keys(payload).length === 0) {
    return 'Not enough structured data to assess value yet. The discovery daemon needs to emit a complete payload before this can be promoted.';
  }
  for (const key of ['valueSummary', 'whyUseful', 'useCase', 'description', 'summary']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  const type = TARGET_TYPE_LABELS[candidate.targetType] ?? candidate.targetType.replaceAll('_', ' ');
  if (candidate.proposedAction === 'update')
    return `Updates an existing ${type} library item with source-backed information.`;
  if (candidate.proposedAction === 'deprecate')
    return `Flags an existing ${type} library item for deprecation based on source-backed evidence.`;
  return `Adds a source-backed ${type} item to the usable libraries after daemon review and human approval.`;
}

export function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  if (status === 'ready_for_human' || status === 'approved' || status === 'applied') return 'success';
  if (status.endsWith('_failed') || status === 'rejected') return 'danger';
  if (status.startsWith('pending')) return 'warning';
  return 'neutral';
}

export function parseCsv(value: string | null): string[] {
  return value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
}

export function typeLabel(value: string): string {
  return TARGET_TYPE_LABELS[value] ?? value.replaceAll('_', ' ');
}

export function libraryItemPath(type: string, id: number, label?: string): string | null {
  if ((type === 'model' || type === 'model_capability') && label)
    return `/agents/libraries/models/${encodeURIComponent(label)}`;
  if (type === 'agent_template' && label) return `/agents/templates/${encodeURIComponent(label)}`;
  if (type === 'skill' && label) return `/agents/skills/${encodeURIComponent(label)}`;
  if (type === 'tool' && label) return `/agents/tools/${encodeURIComponent(label)}`;
  if (type === 'workflow' && Number.isFinite(id)) return `/agents/workflows/${id}`;
  if (type === 'evaluation') return '/admin/evaluations';
  return null;
}

export function findCatalogSourceFragment(
  candidate: Pick<CatalogCandidateListItem, 'integrityChecks' | 'evidenceLinks'>,
): CatalogSourceFragment | null {
  return (
    candidate.evidenceLinks?.sourceFragment ??
    candidate.integrityChecks?.find((check) => check.sourceFragment)?.sourceFragment ??
    null
  );
}

export function buildCatalogSourceEvidenceHref(
  evidence?: Partial<
    Pick<CatalogEvidenceLinks, 'sourceUrl' | 'sourceRepoUrl' | 'sourcePath' | 'sourceRef' | 'sourceFragment'>
  > | null,
  source?: Partial<Pick<CatalogSource, 'sourceUrl' | 'sourceRepoUrl' | 'sourcePath' | 'sourceRef'>> | null,
): string | null {
  const sourceFragment = evidence?.sourceFragment;
  const directUrl = firstNonEmpty(sourceFragment?.sourceUrl, evidence?.sourceUrl, source?.sourceUrl);
  const safeDirectUrl = safeHref(directUrl);
  if (safeDirectUrl) return safeDirectUrl;

  const repoUrl = firstNonEmpty(evidence?.sourceRepoUrl, source?.sourceRepoUrl);
  const sourcePath = firstNonEmpty(sourceFragment?.sourcePath, evidence?.sourcePath, source?.sourcePath);
  const sourceRef = firstNonEmpty(sourceFragment?.sourceRef, evidence?.sourceRef, source?.sourceRef);
  const repoFileUrl = buildGitHubBlobUrl(repoUrl, sourcePath, sourceRef);
  if (repoFileUrl) return repoFileUrl;

  return normalizeGitHubRepoUrl(repoUrl ?? '') ?? safeHref(repoUrl);
}

function buildGitHubBlobUrl(
  repoUrl: string | null,
  sourcePath: string | null,
  sourceRef: string | null,
): string | null {
  if (!repoUrl || !sourcePath || !sourceRef) return null;
  const normalizedRepoUrl = normalizeGitHubRepoUrl(repoUrl);
  if (!normalizedRepoUrl) return null;
  return `${normalizedRepoUrl}/blob/${encodeURIComponent(sourceRef)}/${trimLeadingSlash(sourcePath)}`;
}

function normalizeGitHubRepoUrl(repoUrl: string): string | null {
  const trimmed = repoUrl.trim().replace(/\.git$/i, '');
  if (trimmed.startsWith('https://github.com/') || trimmed.startsWith('http://github.com/')) {
    return trimmed.replace(/^http:\/\//i, 'https://');
  }
  const sshMatch = /^git@github\.com:(.+\/.+)$/i.exec(trimmed);
  if (sshMatch) return `https://github.com/${sshMatch[1]}`;
  return null;
}

function trimLeadingSlash(value: string): string {
  return value.replace(/^\/+/, '');
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}
